import type { MenuProps } from 'antd'
import { Dropdown, Tooltip, Typography } from 'antd'
import { ArrowLeftRight, Check, LogOut, Plus, User } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { OrganizationLong } from 'src/api/main'
import { WorkspaceDemoBadge } from 'src/components'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import { getWorkspaceDemoState } from 'src/utils'

import { useStyles } from './styles'
import {
  formatWorkspaceLabel,
  resolveActiveWorkspace,
  workspaceAccent,
  workspaceInitials,
  WORKSPACE_TILE_ID
} from './utils'

const { Text } = Typography

type Props = {
  collapsed: boolean
  workspaces: OrganizationLong[]
  activeWorkspaceId: number | undefined
}

const SwitcherListItem = ({
  workspace,
  isActive
}: {
  workspace: OrganizationLong
  isActive: boolean
}) => {
  const { styles, cx } = useStyles()
  const label = formatWorkspaceLabel(workspace)
  const accent = workspaceAccent(label)

  return (
    <div className={styles.switcherListItem}>
      <div
        className={cx(
          styles.switcherListAvatar,
          workspace.photo?.file && styles.switcherAvatarPhoto
        )}
        style={{ background: workspace.photo?.file ? undefined : accent }}
        aria-hidden
      >
        {workspace.photo?.file ? (
          <img
            src={workspace.photo.file}
            alt=""
            className={styles.switcherAvatarImage}
          />
        ) : (
          workspaceInitials(label)
        )}
      </div>
      <div className={styles.switcherListItemInfo}>
        <Text strong style={{ fontSize: 12, minWidth: 0 }} ellipsis>
          {label}
        </Text>
      </div>
      {isActive ? <Check size={14} color="var(--ant-color-primary)" /> : null}
    </div>
  )
}

export const WorkspaceSwitcher = ({
  collapsed,
  workspaces,
  activeWorkspaceId
}: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setActiveWorkspaceId, removeUserData } = useSessionContext()

  const active = useMemo(
    () => resolveActiveWorkspace(workspaces, activeWorkspaceId),
    [workspaces, activeWorkspaceId]
  )

  const activeLabel = formatWorkspaceLabel(active ?? undefined)
  const activeAccent = workspaceAccent(activeLabel)
  const activeDemoState = getWorkspaceDemoState(active)

  const items: MenuProps['items'] = useMemo(() => {
    const switchGroup: NonNullable<MenuProps['items']>[number] = {
      type: 'group',
      label: t('shell.switcher.switchWorkspace'),
      children: workspaces.map((w) => ({
        key: `ws-${w.id}`,
        label: (
          <SwitcherListItem workspace={w} isActive={w.id === active?.id} />
        ),
        onClick: () => {
          setActiveWorkspaceId(w.id)
          navigate(paths.workspaces.home(String(w.id)))
        }
      }))
    }

    const footerItems: NonNullable<MenuProps['items']> = [
      { type: 'divider' as const },
      {
        key: 'account-settings',
        icon: <User size={14} />,
        label: t('shell.switcher.accountSettings'),
        onClick: () => navigate(paths.account.profile)
      },
      {
        key: 'create-workspace',
        icon: <Plus size={14} />,
        label: t('shell.switcher.createWorkspace'),
        onClick: () => navigate(paths.workspaces.new)
      },
      {
        key: 'signout',
        icon: <LogOut size={14} />,
        danger: true,
        label: t('shell.switcher.signOut'),
        onClick: () => removeUserData()
      }
    ]

    return [switchGroup, ...footerItems]
  }, [t, workspaces, active, setActiveWorkspaceId, navigate, removeUserData])

  // Brand-new account with no workspaces: show a placeholder that routes to the
  // create flow instead of an empty sidebar slot. Gate on the real "zero
  // workspaces" signal — a user who owns workspaces but has none resolved as
  // active should keep the old behavior (render nothing), not a misleading CTA.
  if (!active) {
    if (workspaces.length > 0) {
      return null
    }

    const placeholder = (
      <button
        type="button"
        className={cx(
          styles.switcherTrigger,
          collapsed && styles.switcherTriggerCollapsed
        )}
        onClick={() => navigate(paths.workspaces.new)}
        aria-label={t('shell.switcher.createWorkspace')}
      >
        <span className={styles.switcherPlaceholderAvatar} aria-hidden>
          <Plus size={15} />
        </span>
        {!collapsed ? (
          <span className={styles.switcherInfo}>
            <span className={styles.switcherName}>
              {t('shell.switcher.noWorkspace')}
            </span>
            <span className={styles.switcherMeta}>
              {t('shell.switcher.noWorkspaceMeta')}
            </span>
          </span>
        ) : null}
      </button>
    )

    return collapsed ? (
      <Tooltip title={t('shell.switcher.createWorkspace')} placement="right">
        {placeholder}
      </Tooltip>
    ) : (
      placeholder
    )
  }

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      placement="bottomLeft"
      classNames={{ root: styles.switcherDropdown }}
    >
      <button
        type="button"
        className={cx(
          styles.switcherTrigger,
          collapsed && styles.switcherTriggerCollapsed
        )}
        aria-label={t('shell.switcher.ariaLabel')}
      >
        <span
          id={WORKSPACE_TILE_ID}
          className={cx(
            styles.switcherAvatar,
            active.photo?.file && styles.switcherAvatarPhoto
          )}
          // Flat, exactly like `AvatarWithInitials` paints the same name — the
          // trigger tile must match the overview hero and the onboarding
          // preview, and a gradient of the same colour reads as a different
          // tile at this size.
          style={{
            background: active.photo?.file ? undefined : activeAccent
          }}
          aria-hidden
        >
          {active.photo?.file ? (
            <img
              src={active.photo.file}
              alt=""
              className={styles.switcherAvatarImage}
            />
          ) : (
            workspaceInitials(activeLabel)
          )}
        </span>
        {!collapsed ? (
          <>
            <span className={styles.switcherInfo}>
              <span className={styles.switcherName} title={activeLabel}>
                {activeLabel}
              </span>
              <span className={styles.switcherMeta}>{active.domain_name}</span>
            </span>
            <span className={styles.switcherDemoBadge}>
              <WorkspaceDemoBadge
                isDemo={activeDemoState.isDemo}
                expiresAt={activeDemoState.expiresAt}
                compact
              />
            </span>
            <ArrowLeftRight size={12} className={styles.switcherChevron} />
          </>
        ) : null}
      </button>
    </Dropdown>
  )
}
